import { Box, ButtonBase } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext.tsx";
import { useGuide, useMarkersShown } from "./GuideContext.tsx";

/** How strongly a marker is washed with the primary colour, and when pointed at. */
const MARKER_ALPHA = 0.1;
const POINTED_ALPHA = 0.25;

const SIZE = 22;

/** How a marker looks: its number, dark while its entry is open. */
export const useMarkerLook = (picked: boolean, pointed: boolean) => {
  const { theme } = useTheme();
  const { main, contrastText } = theme.palette.primary;
  return {
    width: SIZE,
    height: SIZE,
    flex: "none",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    typography: "caption",
    fontWeight: 700,
    lineHeight: 1,
    bgcolor: picked
      ? main
      : alpha(main, pointed ? POINTED_ALPHA : MARKER_ALPHA),
    color: picked ? contrastText : main,
  };
};

/** The number of an entry, in the panel where the marker is no button. */
export const MarkerNumber = ({
  picked,
  pointed,
  children,
}: {
  picked: boolean;
  pointed: boolean;
  children: ReactNode;
}) => (
  <Box component="span" aria-hidden sx={useMarkerLook(picked, pointed)}>
    {children}
  </Box>
);

/** What an element of the page is called, with its marker beside it. */
export const Marked = ({
  entry,
  children,
}: {
  // none, where another element carries the marker
  entry: string | undefined;
  children: ReactNode;
}) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
  >
    {children}
    {entry !== undefined && <GuideMarker entry={entry} />}
  </Box>
);

/**
 * Sits next to an element the guide explains while the guide is open, and
 * leads to its entry. Pointing at it lights the entry up, and the other way
 * round.
 */
export const GuideMarker = ({ entry }: { entry: string }) => {
  const guide = useGuide();
  const markersShown = useMarkersShown();
  const { t } = useTranslation();
  const picked = guide?.picked === entry;
  const look = useMarkerLook(picked, guide?.pointed === entry);
  // the element is on the page, so its entry is listed, open or not
  const register = guide?.register;
  useEffect(() => register?.(entry), [register, entry]);
  if (guide === undefined || !markersShown) return null;
  const { page, shown, dispatch } = guide;
  if (page === undefined || !shown) return null;
  const number = guide.entries.indexOf(entry) + 1;
  if (number === 0) return null;

  const point = (pointed?: string) =>
    dispatch({ type: "point", entry: pointed });
  return (
    <ButtonBase
      aria-label={t("guide.explain", {
        title: t(`guide.pages.${page}.entries.${entry}.title`),
      })}
      aria-controls={`guide-${entry}`}
      aria-expanded={picked}
      onClick={(event) => {
        // a marker can sit on a row that opens something of its own
        event.stopPropagation();
        dispatch({ type: "pick", page, entry });
      }}
      onMouseEnter={() => point(entry)}
      onMouseLeave={() => point()}
      onFocus={() => point(entry)}
      onBlur={() => point()}
      sx={look}
    >
      {number}
    </ButtonBase>
  );
};
